import { cn } from '@/lib/utils'

/* ── Mini Card ── */
export interface CardProps {
  rank: string
  suit: 'spade' | 'heart' | 'club' | 'diamond'
}

const suitSymbol: Record<CardProps['suit'], string> = { spade: '\u2660', heart: '\u2665', club: '\u2663', diamond: '\u2666' }

export function MiniCard({ rank, suit }: CardProps) {
  const red = suit === 'heart' || suit === 'diamond'
  return (
    <div className={cn(
      'w-7 h-10 rounded-md flex flex-col items-center justify-center text-[10px] font-bold leading-tight border',
      red ? 'bg-red-950/60 border-red-500/30 text-red-400' : 'bg-slate-800/60 border-slate-500/30 text-slate-300'
    )}>
      <span>{rank}</span>
      <span className="text-[9px]">{suitSymbol[suit]}</span>
    </div>
  )
}

/* ── Types ── */
export interface StreetAction {
  player: string
  action: string
  amount?: string
}

export interface Street {
  name: string
  communityCards: CardProps[]
  actions: StreetAction[]
}

export interface PotLine {
  label: string
  amount: number
}

export interface HandTimelineData {
  streets: Street[]
  potDistribution?: PotLine[]
  totalPot?: number
}

/* ── Action Badge ── */
function actionStyle(action: string) {
  if (action === 'fold') return 'bg-white/5 text-muted-foreground'
  if (action === 'all-in') return 'bg-red-500/10 text-red-400'
  if (action === 'check') return 'bg-white/5 text-muted-foreground'
  return 'bg-gold/10 text-gold'
}

/* ── Street-by-Street Timeline ── */
export function HandTimeline({ streets, potDistribution, totalPot }: HandTimelineData) {
  return (
    <div className="space-y-5">
      {/* Street Actions */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">行動紀錄</h4>
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-[5px] top-3 bottom-3 w-px bg-white/10" />

          <div className="space-y-4">
            {streets.map((street) => (
              <div key={street.name} className="relative pl-6">
                {/* Dot on the timeline */}
                <div className={cn(
                  'absolute left-0 top-0.5 w-[11px] h-[11px] rounded-full border-2',
                  street.name === 'Preflop' ? 'bg-blue-500 border-blue-400' :
                  street.name === 'Flop' ? 'bg-emerald-500 border-emerald-400' :
                  street.name === 'Turn' ? 'bg-amber-500 border-amber-400' :
                  'bg-rose-500 border-rose-400'
                )} />

                <div className="space-y-2">
                  {/* Street header + new community cards */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn(
                      'text-xs font-bold',
                      street.name === 'Preflop' ? 'text-blue-400' :
                      street.name === 'Flop' ? 'text-emerald-400' :
                      street.name === 'Turn' ? 'text-amber-400' :
                      'text-rose-400'
                    )}>{street.name}</span>
                    {street.communityCards.length > 0 && (
                      <div className="flex gap-1">
                        {street.communityCards.map((c, i) => <MiniCard key={i} {...c} />)}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="space-y-1">
                    {street.actions.length > 0 ? street.actions.map((act, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-bold text-foreground w-20 truncate">{act.player}</span>
                        <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold', actionStyle(act.action))}>
                          {act.action}{act.amount ? ` ${act.amount}` : ''}
                        </span>
                      </div>
                    )) : (
                      <p className="text-[10px] text-muted-foreground italic">（無行動 - 攤牌）</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pot Distribution */}
      {potDistribution && potDistribution.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">底池分配</h4>
          <div className="p-4 rounded-xl bg-surface-elevated space-y-2">
            {potDistribution.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{item.label}</span>
                <span className="text-sm font-bold font-mono text-gold">${(item.amount / 100).toFixed(2)}</span>
              </div>
            ))}
            {totalPot != null && (
              <>
                <div className="h-px bg-white/10 my-1" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-foreground">總計</span>
                  <span className="text-sm font-bold font-mono text-gold">${(totalPot / 100).toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
